import { useContext, useEffect, useRef } from 'react';
import { useActionData, useNavigate, useNavigation, Form, Link, useSearchParams } from 'react-router-dom';
import Field from '../components/form/Field.jsx'
import Button from '../components/Button.jsx'
import AccountNotConfirmedWarning from '../components/AccountNotConfirmedWarning.jsx'
import ReactRecaptcha3 from 'react-google-recaptcha3'
import { AuthContext } from '../store/auth-context.js'
import { GoogleOAuthProvider, useGoogleLogin } from "@react-oauth/google";
import { SignInSession, GoogleSignInSession, AppleSignInSession, GetCurrentUserDataSession } from '../repo/SessionsRepo.js'
import AppleSignin from 'react-apple-signin-auth';
import { config } from '../config';

function SignInPage() {
  const emailRef = useRef();
  const responseData = useActionData()
  const signInNavigation = useNavigation()
  const isSubmitting = signInNavigation.state === 'submitting'

  const authCtx = useContext(AuthContext)
  const navigate = useNavigate()

  // The router action performs the sign-in and hands back the token and user
  // (actions run outside React and cannot touch context). Here, inside the
  // component, we store them in the auth context and then navigate home.
  useEffect(() => {
    if (responseData?.success) {
      authCtx.authenticate(responseData.token, responseData.user)
      navigate('/')
    }
  }, [responseData, authCtx, navigate])

  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('token') || null
  const inviteGroupID = searchParams.get('group_id') || null

  const buildUrlWithInvite = (url) => {
    if (inviteToken) {
      url = url + '?token=' + inviteToken + '&group_id=' + inviteGroupID
    }

    return url
  }

  const GoogleLoginButton = () => {
    const googleLogin = useGoogleLogin({
      onSuccess: async (response) => {
        document.getElementById('apple_token').value = '';
        document.getElementById('google_token').value = response.code
        document.getElementById('sign_in_button').click()
      },
      onError: () => {
        console.log("Login Failed")
      },
      flow: "auth-code"
    });

    return (
      <div className="py-2.5 px-5 me-2 mb-2 text-sm font-medium text-gray-900 focus:outline-none bg-white rounded-lg border border-gray-200 hover:bg-gray-100 hover:text-blue-700 focus:z-10 focus:ring-4 focus:ring-gray-100" disabled={isSubmitting} id="google_sign_in_button" onClick={googleLogin}>
        <div className="flex items-center justify-center space-x-2">
          <svg aria-hidden="true" className="native svg-icon iconGoogle" width="18" height="18" viewBox="0 0 18 18"><path fill="#4285F4" d="M16.51 8H8.98v3h4.3c-.18 1-.74 1.48-1.6 2.04v2.01h2.6a7.8 7.8 0 0 0 2.38-5.88c0-.57-.05-.66-.15-1.18"></path><path fill="#34A853" d="M8.98 17c2.16 0 3.97-.72 5.3-1.94l-2.6-2a4.8 4.8 0 0 1-7.18-2.54H1.83v2.07A8 8 0 0 0 8.98 17"></path><path fill="#FBBC05" d="M4.5 10.52a4.8 4.8 0 0 1 0-3.04V5.41H1.83a8 8 0 0 0 0 7.18z"></path><path fill="#EA4335" d="M8.98 4.18c1.17 0 2.23.4 3.06 1.2l2.3-2.3A8 8 0 0 0 1.83 5.4L4.5 7.49a4.8 4.8 0 0 1 4.48-3.3"></path></svg>
          <span>Entrar com Google</span>
        </div>
      </div>
    )
  }

  useEffect(() => {
    ReactRecaptcha3.init(config.recaptchaPublicKey).then(status => {
      if (status !== 'success') {
        console.log("Error! Could not load Recaptcha.")
      }
    })
  }, [])

  const translateError = (error) => {
    switch (error) {
      case 'invalid credentials':
        return 'Email ou senha inválida.'
      case 'account not confirmed':
        return 'Sua conta ainda não foi confirmada. Caso não tenha recebido o email de confirmação, verifique sua caixa de spam ou clique=' + <Link to="/sign_up" >aqui</Link> + 'para reenviarmos o email.'
      case 'your account is locked because of multiple invalid sign in attempts try again in 1 minute':
        return 'Sua conta foi bloqueada devido a várias tentativas de login inválidas. Tente novamente em 1 minuto ou verifique seu email com as instruções de como desbloquear sua conta.'
      case 'your account is locked because of multiple invalid sign in attempts try again in 2 minutes':
        return 'Sua conta foi bloqueada devido a várias tentativas de login inválidas. Tente novamente em 2 minutos ou verifique seu email com as instruções de como desbloquear sua conta.'
      case 'your account is locked because of multiple invalid sign in attempts try again in 5 minutes':
        return 'Sua conta foi bloqueada devido a várias tentativas de login inválidas. Tente novamente em 5 minutos ou verifique seu email com as instruções de como desbloquear sua conta.'
      case 'your account is locked because of multiple invalid sign in attempts try again in 7 minutes':
        return 'Sua conta foi bloqueada devido a várias tentativas de login inválidas. Tente novamente em 7 minutos ou verifique seu email com as instruções de como desbloquear sua conta.'
      case 'your account is locked because of multiple invalid sign in attempts try again in 10 minutes':
        return 'Sua conta foi bloqueada devido a várias tentativas de login inválidas. Tente novamente em 10 minutos ou verifique seu email com as instruções de como desbloquear sua conta.'
      default:
        document.getElementById('apple_token').value = ''
        document.getElementById('google_token').value = ''
        return 'Erro desconhecido'
    }
  }

  return (
    <section>
      <>
      { (responseData && responseData.errors && responseData.errors.detail === 'account not confirmed') ? (
        <AccountNotConfirmedWarning email={emailRef.current.value}/>
      ) : (
      <div className="flex flex-col items-center justify-center px-6 py-10 mx-auto lg:py-0">
        <h1 className="flex items-center lg:mt-24 mb-2 text-2xl font-semibold text-gray-900">
          Entrar
        </h1>
        <div className="w-full bg-white rounded-lg shadow md:mt-0 sm:max-w-md xl:p-0">
          <div className="p-6 space-y-4 md:space-y-6 sm:p-8">
            <div className="w-full mx-auto">
              <GoogleOAuthProvider clientId={config.googleApiToken}>
                <GoogleLoginButton />
              </GoogleOAuthProvider>
            </div>

            <div className="w-full mx-auto">
              <AppleSignin
                authOptions={{
                  clientId: config.appleApiToken,
                  scope: 'email name',
                  redirectURI: config.appleRedirectUri,
                  state: 'state',
                  nonce: 'nonce',
                  usePopup: true
                }}
                uiType="dark"
                noDefaultStyle={true}
                onSuccess={(response) => {
                  document.getElementById('google_token').value = ''
                  document.getElementById('apple_token').value = response.authorization.id_token;
                  document.getElementById('sign_in_button').click();
                }}
                onError={(error) => {
                  console.error(error);
                }}
                render={({ onClick }) => (
                  <div className="py-2.5 px-5 me-2 mb-2 text-sm font-medium text-gray-900 focus:outline-none bg-white rounded-lg border border-gray-200 hover:bg-gray-100 hover:text-blue-700 focus:z-10 focus:ring-4 focus:ring-gray-100" disabled={isSubmitting} id="google_sign_in_button" onClick={onClick}>
                    <div className="flex items-center justify-center space-x-2">
                      <svg width="20px" height="20px" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg"><title>ionicons-v5_logos</title><path d="M349.13,136.86c-40.32,0-57.36,19.24-85.44,19.24C234.9,156.1,212.94,137,178,137c-34.2,0-70.67,20.88-93.83,56.45-32.52,50.16-27,144.63,25.67,225.11,18.84,28.81,44,61.12,77,61.47h.6c28.68,0,37.2-18.78,76.67-19h.6c38.88,0,46.68,18.89,75.24,18.89h.6c33-.35,59.51-36.15,78.35-64.85,13.56-20.64,18.6-31,29-54.35-76.19-28.92-88.43-136.93-13.08-178.34-23-28.8-55.32-45.48-85.79-45.48Z"/><path d="M340.25,32c-24,1.63-52,16.91-68.4,36.86-14.88,18.08-27.12,44.9-22.32,70.91h1.92c25.56,0,51.72-15.39,67-35.11C333.17,85.89,344.33,59.29,340.25,32Z"/></svg>
                      <span>Entrar com Apple</span>
                    </div>
                  </div>
                )}
              />
            </div>

            <div className="py-3 flex items-center text-xs text-gray-500 uppercase before:flex-1 before:border-t before:border-gray-200 before:me-6 after:flex-1 after:border-t after:border-gray-200 after:ms-6">OU</div>

            <Form method="POST" className="space-y-4 md:space-y-6" id="sign_in_form">
              <Field type={"email"} name={"email"} id={"email"} label={"Email"} required={""} ref={emailRef}/>
              <Field type={"password"} name={"password"} id={"password"} label={"Senha"} required={""}/>
              {responseData && responseData.errors && (
                <p className={"mt-2 text-sm text-red-600"}>
                  {translateError(responseData.errors.detail)}
                </p>
              )}
              {inviteToken && (
                <Field type={"hidden"} name={"invite_token"} id={"invite_token"} value={inviteToken}/>
              )}
              {inviteGroupID && (
                <Field type={"hidden"} name={"invite_group_id"} id={"invite_group_id"} value={inviteGroupID}/>
              )}
              <Field type={"hidden"} name={"google_token"} id={"google_token"}/>
              <Field type={"hidden"} name={"apple_token"} id={"apple_token"}/>
              <Button styleName="green" className="w-full" disabled={isSubmitting} id="sign_in_button">
                {isSubmitting ? 'Entrando...' : 'Entrar'}
              </Button>

              <div className="text-center text-sm">
                <Link className="font-medium text-green-600 hover:underline" to={"/accounts/password/request_recovery"}>Esqueci a senha</Link>
                <span> ou </span>
                <Link className="font-medium text-green-600 hover:underline" to={buildUrlWithInvite("/sign_up")}>fazer cadastro</Link>
              </div>
            </Form>
          </div>
        </div>
      </div>
      )}
      </>
    </section>
  )
}

export default SignInPage

async function getRecaptchaToken() {
  let recaptchaToken = '';

  await ReactRecaptcha3.getToken().then(token => {
    recaptchaToken = token
  }, error => {
    console.log("ERROR!", error)
  })

  return recaptchaToken;
}

export async function action({request}) {
  const formData = await request.formData()
  const data = Object.fromEntries(formData.entries())
  if (data['google_token']) {
    return await googleSignInAction(data) 
  } else if (data['apple_token']) {
    return await appleSignInAction(data)
  } else {
    return await signInAction(data)
  }
}

async function signInAction(data) {
  const recaptchaToken = await getRecaptchaToken()

  let signInData = {
    email: data['email'],
    password: data['password'],
    captcha_token: recaptchaToken,
    invite: {}
  }

  if (data['invite_token']) {
    signInData['invite']['token'] = data['invite_token']
  }

  if (data['invite_group_id']) {
    signInData['invite']['group_id'] = data['invite_group_id']
  }

  const response = await SignInSession(signInData)
  return await handleSignInAction(response)
}

async function googleSignInAction(data) {
  let signInData = {
    google_token: data['google_token'],
    invite: {}
  }

  if (data['invite_token']) {
    signInData['invite']['token'] = data['invite_token']
  }

  if (data['invite_group_id']) {
    signInData['invite']['group_id'] = data['invite_group_id']
  }

  const response = await GoogleSignInSession(signInData)

  return await handleSignInAction(response)
}

async function appleSignInAction(data) {
  let signInData = {
    apple_token: data['apple_token'],
    invite: {}
  }

  if (data['invite_token']) {
    signInData['invite']['token'] = data['invite_token']
  }

  if (data['invite_group_id']) {
    signInData['invite']['group_id'] = data['invite_group_id']
  }

  const response = await AppleSignInSession(signInData)

  return await handleSignInAction(response)
}

async function handleSignInAction(response) {
  const status = response.status
  const resData = response.data

  if (status === 400 || status === 401 || status === 423) {
    return resData
  }

  // Fetch the current user with the freshly issued token, then hand both back
  // to the component so it can store them in the auth context.
  const token = resData.data.token
  const userResponse = await GetCurrentUserDataSession(token)

  return { success: true, token, user: userResponse.data }
}